import classNames from 'classnames';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';

import { DEPTH, POSITION_SIDE } from 'src/common/constants';
import { changeScrollBehavior, currencyFormatter } from 'src/common/helpers';
import { Tooltip } from 'src/components/core';
import { Button } from 'src/components/core/button';
import { Edit, Share } from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { StyledCards } from 'src/components/features/common/card/styledCards';
import { PositionLimitCloseModal } from 'src/components/features/derivative/modals/positionLimitCloseModal';
import { PositionMarginModal } from 'src/components/features/derivative/modals/positionMarginModal';
import { PositionMarketCloseModal } from 'src/components/features/derivative/modals/positionMarketCloseModal';
import { PositionTPSLModal } from 'src/components/features/derivative/modals/positionTPSLModal';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { APP_URLS } from 'src/setup/client/app-urls';
import { fetchPositionStopProfitLoss } from 'src/setup/redux/slices/derivativesSlice';
import { FutureSymbol, OrderTableContentProps, Position, StopProfitLossPayLoad } from 'src/types';

import { ModalWithDrawerMobile } from '../../modals/shareProfitLossModalMobile';

const StyledPositionTitle = styled.div`
    cursor: pointer;
`;

const PositionCard: FC<OrderTableContentProps> = ({ isShowAll }) => {
    const { t } = useTranslation();
    const { colors } = useTheme();
    const navigate = useNavigate();
    const [filterPositions, setFilterPositions] = useState([] as Array<Position>);
    const currentSymbol: FutureSymbol = useAppSelector((state) => state.derivatives.currentSymbol);
    const symbolId = currentSymbol.symbolId;
    const { positions, positionTrigger, shortPositionStopProfitLoss, longPositionStopProfitLoss } =
        useAppSelector((state) => state.derivatives);
    const slowBroker = useAppSelector((state) => state.socket.slowBroker);
    const [isPositionMarketCloseModalOpen, setIsPositionMarketCloseModalOpen] = useState(false);
    const [isPositionLimitCloseModalOpen, setIsPositionLimitCloseModalOpen] = useState(false);
    const [isPositionTPSLModalOpen, setIsPositionTPSLModalOpen] = useState(false);
    const [isPositionMarginModalOpen, setIsPositionMarginModalOpen] = useState(false);
    const [isProfitLossShareModalOpen, setIsProfitLossShareModalOpen] = useState(false);
    const [currentModalPosition, setCurrentModalPosition] = useState({} as Position);
    const futureSymbols = useAppSelector((state) => state.global.configV2.futuresSymbol);
    const { userCurrentFutureTradeAccountIndex } = useAppSelector((state) => state.global);
    const dispatch = useAppDispatch();

    useEffect(() => {
        let filtered = isShowAll
            ? positions
            : positions.filter((position) => position.symbolId === symbolId);
        filtered = [...filtered].sort((a, b) => Number(b.positionId) - Number(a.positionId));
        setFilterPositions(filtered);
    }, [positions, symbolId, isShowAll]);

    useEffect(() => {
        let filtered = isShowAll
            ? positions
            : positions.filter((position) => position.symbolId === symbolId);
        filtered.forEach((position: Position) => {
            let body: StopProfitLossPayLoad = {
                symbol_id: position.symbolId,
                is_long: position.isLong,
                account_index: userCurrentFutureTradeAccountIndex,
            };
            dispatch(fetchPositionStopProfitLoss(body));
        });
    }, [positionTrigger, isShowAll, userCurrentFutureTradeAccountIndex]);

    const openPositionMarketCloseModal = (position: Position) => {
        setCurrentModalPosition(position);
        setIsPositionMarketCloseModalOpen(!isPositionMarketCloseModalOpen);
    };

    const openPositionLimitCloseModal = (position: Position) => {
        setCurrentModalPosition(position);
        setIsPositionLimitCloseModalOpen(!isPositionLimitCloseModalOpen);
    };

    const openPositionTPSLModal = (position: Position) => {
        setCurrentModalPosition(position);
        setIsPositionTPSLModalOpen(!isPositionTPSLModalOpen);
    };

    const openPositionMarginModal = (position: Position) => {
        setCurrentModalPosition(position);
        setIsPositionMarginModalOpen(!isPositionMarginModalOpen);
    };

    const openShareProfitLossModal = (position: Position) => {
        setCurrentModalPosition(position);
        setIsProfitLossShareModalOpen(!isProfitLossShareModalOpen);
    };

    const changePair = (symbolId: string) => {
        navigate(`${APP_URLS.CONTRACT}/${symbolId}`);
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    };

    const columns = [
        {
            title: (
                <FlexContainer styles={{ alignItems: 'center' }}>{t('contracts')}</FlexContainer>
            ),
            key: 'contracts',
            render: (row: { [x: string]: any }) => {
                return (
                    <StyledPositionTitle
                        className={classNames(
                            'contract',
                            { borderGreen: row['isLong'] === POSITION_SIDE.LONG },
                            { borderRed: row['isLong'] === POSITION_SIDE.SHORT },
                        )}
                        onClick={() => changePair(row.symbolId)}
                    >
                        {row['symbolName']}
                        <br />
                        <span>{`${t('leverage')} ${row['leverage']}x`}</span>
                    </StyledPositionTitle>
                );
            },
        },
        {
            title: t('qty'),
            key: 'qty',
            render: (row: { [x: string]: any }) => {
                return (
                    <span
                        className={classNames(
                            { greenText: row['isLong'] === POSITION_SIDE.LONG },
                            { redText: row['isLong'] === POSITION_SIDE.SHORT },
                        )}
                    >
                        {currencyFormatter(
                            row['available'],
                            DEPTH[futureSymbols[row.symbolId]?.basePrecision],
                        )}
                    </span>
                );
            },
        },
        {
            title: t('value'),
            key: 'value',
            render: (row: { [x: string]: any }) => {
                return (
                    <span>
                        {currencyFormatter(
                            row['positionValues'],
                            DEPTH[
                                Number(
                                    futureSymbols[row.symbolId]?.baseTokenFutures?.marginPrecision,
                                )
                            ],
                        )}{' '}
                        {row['unit']}
                    </span>
                );
            },
        },
        {
            title: (
                <Tooltip overlay={t('entry_price_description')} interactive>
                    <span style={{ cursor: 'pointer' }}>{t('entry_price')}</span>
                </Tooltip>
            ),
            key: 'entryPrice',
            render: (row: { [x: string]: any }) => {
                return (
                    <span>
                        {currencyFormatter(
                            row['avgPrice'],
                            DEPTH[futureSymbols[row.symbolId]?.minPricePrecision],
                        )}
                    </span>
                );
            },
        },
        {
            title: t('index_price'),
            key: 'indexPrice',
            width: 80,
            render: (row: { [x: string]: any }) => {
                return (
                    <span>
                        {currencyFormatter(
                            row['indices'],
                            DEPTH[futureSymbols[row.symbolId]?.minPricePrecision],
                        )}
                    </span>
                );
            },
        },
        {
            title: (
                <Tooltip overlay={t('liq_price_description')} interactive>
                    <a href={APP_URLS.LIQ_PRICE_HELP} target="_blank" rel="noopener noreferrer">
                        {t('liq_price')}
                    </a>
                </Tooltip>
            ),
            key: 'liqPrice',
            width: 71,
            render: (row: { [x: string]: any }) => {
                return (
                    <span className="yelloText">
                        {currencyFormatter(
                            row['liquidationPrice'],
                            DEPTH[futureSymbols[row.symbolId]?.minPricePrecision],
                        )}
                    </span>
                );
            },
        },
        {
            title: (
                <Tooltip overlay={t('position_margin_description')} interactive>
                    <a
                        href={APP_URLS.POSITION_MARGIN_HELP}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t('position_margin')}
                    </a>
                </Tooltip>
            ),
            key: 'positionMargin',
            width: 108,
            render: (row: { [x: string]: any }) => {
                return (
                    <FlexContainer>
                        <span>
                            {currencyFormatter(
                                row['margin'],
                                DEPTH[
                                    Number(
                                        futureSymbols[row.symbolId]?.baseTokenFutures
                                            ?.marginPrecision,
                                    )
                                ],
                            )}{' '}
                            {row['unit']}
                            <br />≈{currencyFormatter(row['margin'], 2)} {'USD'}
                        </span>
                        <Button
                            variant="outlined"
                            size="small"
                            className="action-button"
                            styles={{
                                marginLeft: '6px',
                                padding: '0px !important',
                                borderRadius: '3px !important',
                            }}
                            onClick={() => {
                                openPositionMarginModal(row as Position);
                            }}
                        >
                            <Edit
                                fill={colors.text.tertiary}
                                style={{ width: '16px', height: '16px' }}
                            />
                        </Button>
                    </FlexContainer>
                );
            },
        },
        {
            title: t('unrealized_pl'),
            key: 'unrealisedPnl',
            width: 129,
            render: (row: { [x: string]: any }) => {
                const value = row['unrealisedPnl'];
                const positionSymbolId = row['symbolId'];
                const marketPrice = slowBroker[positionSymbolId]
                    ? Number(slowBroker[positionSymbolId].c)
                    : 0.0;
                const avgPrice = row['avgPrice'];
                const leverage = row['leverage'];
                const priceDiffeence =
                    row['isLong'] === POSITION_SIDE.LONG
                        ? marketPrice - avgPrice
                        : avgPrice - marketPrice;
                const profitPercentage = (priceDiffeence / avgPrice) * 100 * leverage;
                return (
                    <FlexContainer styles={{ gap: 4, alignItems: 'center', justifyContent: 'end' }}>
                        <span
                            className={classNames(
                                { greenText: value >= 0 },
                                { redText: value < 0 },
                            )}
                        >
                            {currencyFormatter(
                                value,
                                DEPTH[
                                    Number(
                                        futureSymbols[row.symbolId]?.baseTokenFutures
                                            ?.marginPrecision,
                                    )
                                ],
                            )}{' '}
                            {row['unit']}
                            <br />(
                            {slowBroker[positionSymbolId]
                                ? `${currencyFormatter(profitPercentage, 2)}%`
                                : '--'}
                            ) ≈{currencyFormatter(value, 2)} {'USD'}
                        </span>
                        <Share
                            fill={colors.text.tertiary}
                            style={{ cursor: 'pointer', flexShrink: 0 }}
                            onClick={() => {
                                openShareProfitLossModal({
                                    ...row,
                                    profitPercentage,
                                    lastPrice: marketPrice,
                                } as Position);
                            }}
                        />
                    </FlexContainer>
                );
            },
        },
        {
            title: (
                <Tooltip overlay={t('realized_pnl_description')} interactive>
                    <a href={APP_URLS.REALIZED_PL_HELP} target="_blank" rel="noopener noreferrer">
                        {t('realized_pl')}
                    </a>
                </Tooltip>
            ),
            key: 'realisedPnl',
            render: (row: { [x: string]: any }) => {
                const value = row['realisedPnl'];
                return (
                    <span className={classNames({ greenText: value >= 0 }, { redText: value < 0 })}>
                        {currencyFormatter(
                            value,
                            DEPTH[
                                Number(
                                    futureSymbols[row.symbolId]?.baseTokenFutures?.marginPrecision,
                                )
                            ],
                        )}{' '}
                        {row['unit']}
                        <br />≈{currencyFormatter(value, 2)} {'USD'}
                    </span>
                );
            },
        },
        {
            title: t('tp_sl'),
            key: 'tpSl',
            render: (row: { [x: string]: any }) => {
                const positionSymbolId = row['symbolId'];
                const positionStopProfitLoss =
                    row['isLong'] === POSITION_SIDE.LONG
                        ? longPositionStopProfitLoss[positionSymbolId]
                        : shortPositionStopProfitLoss[positionSymbolId];
                return positionStopProfitLoss &&
                    (positionStopProfitLoss.stopProfit || positionStopProfitLoss.stopLoss) ? (
                    <FlexContainer
                        styles={{
                            justifyContent: 'flex-end',
                        }}
                    >
                        <span>
                            <span className="greenText">
                                {positionStopProfitLoss.stopProfit
                                    ? currencyFormatter(
                                          Number(positionStopProfitLoss.stopProfitPrice),
                                          DEPTH[
                                              Number(futureSymbols[row.symbolId]?.minPricePrecision)
                                          ],
                                      )
                                    : '--'}
                            </span>
                            {positionStopProfitLoss.stopProfit &&
                            positionStopProfitLoss.stopLoss ? (
                                <br />
                            ) : (
                                <span className="greyText">{'/'}</span>
                            )}
                            <span className="redText">
                                {positionStopProfitLoss.stopLoss
                                    ? currencyFormatter(
                                          Number(positionStopProfitLoss.stopLossPrice),
                                          DEPTH[
                                              Number(futureSymbols[row.symbolId]?.minPricePrecision)
                                          ],
                                      )
                                    : '--'}
                            </span>
                        </span>
                        <Button
                            variant="outlined"
                            size="small"
                            className="action-button"
                            styles={{
                                marginLeft: '6px',
                                padding: '0px !important',
                                borderRadius: '3px !important',
                            }}
                            onClick={() => {
                                openPositionTPSLModal(row as Position);
                            }}
                        >
                            <Edit
                                fill={colors.text.tertiary}
                                style={{ width: '16px', height: '16px' }}
                            />
                        </Button>
                    </FlexContainer>
                ) : (
                    <Button
                        variant="outlined"
                        className="action-button"
                        styles={{ float: 'right', borderRadius: '3px !important' }}
                        onClick={() => {
                            openPositionTPSLModal(row as Position);
                        }}
                    >
                        {t('add')}
                    </Button>
                );
            },
        },
        {
            title: t('close_by'),
            key: 'closeBy',
            // eslint-disable-next-line no-unused-vars
            render: (row: { [x: string]: any }) => {
                return (
                    <FlexContainer styles={{ justifyContent: 'flex-start' }}>
                        <Button
                            variant="outlined"
                            className="action-button"
                            onClick={() => {
                                openPositionLimitCloseModal(row as Position);
                            }}
                        >
                            {t('limit')}
                        </Button>
                        <Button
                            variant="outlined"
                            className="action-button"
                            styles={{ marginLeft: '6px' }}
                            onClick={() => {
                                openPositionMarketCloseModal(row as Position);
                            }}
                        >
                            {t('market')}
                        </Button>
                    </FlexContainer>
                );
            },
        },
    ];
    return (
        <>
            <StyledCards columns={columns} data={filterPositions} />{' '}
            {isPositionMarketCloseModalOpen && (
                <PositionMarketCloseModal
                    open={isPositionMarketCloseModalOpen}
                    position={currentModalPosition}
                    handleClose={() => {
                        changeScrollBehavior(!isPositionMarketCloseModalOpen);
                        setIsPositionMarketCloseModalOpen(!isPositionMarketCloseModalOpen);
                    }}
                />
            )}
            {isPositionLimitCloseModalOpen && (
                <PositionLimitCloseModal
                    open={isPositionLimitCloseModalOpen}
                    lastPrice={Number(slowBroker[currentModalPosition.symbolId].c)}
                    position={currentModalPosition}
                    handleClose={() => {
                        changeScrollBehavior(!isPositionLimitCloseModalOpen);
                        setIsPositionLimitCloseModalOpen(!isPositionLimitCloseModalOpen);
                    }}
                />
            )}
            {isPositionTPSLModalOpen && (
                <PositionTPSLModal
                    open={isPositionTPSLModalOpen}
                    position={currentModalPosition}
                    positionStopProfitLoss={
                        currentModalPosition.isLong === POSITION_SIDE.LONG
                            ? longPositionStopProfitLoss[currentModalPosition.symbolId]
                            : shortPositionStopProfitLoss[currentModalPosition.symbolId]
                    }
                    handleClose={() => {
                        changeScrollBehavior(!isPositionTPSLModalOpen);
                        setIsPositionTPSLModalOpen(!isPositionTPSLModalOpen);
                    }}
                />
            )}
            {isPositionMarginModalOpen && (
                <PositionMarginModal
                    open={isPositionMarginModalOpen}
                    position={currentModalPosition}
                    handleClose={() => {
                        changeScrollBehavior(!isPositionMarginModalOpen);
                        setIsPositionMarginModalOpen(!isPositionMarginModalOpen);
                    }}
                />
            )}
            {isProfitLossShareModalOpen && (
                <ModalWithDrawerMobile
                    open={isProfitLossShareModalOpen}
                    position={currentModalPosition}
                    isActive
                    handleClose={() => {
                        changeScrollBehavior(!isProfitLossShareModalOpen);
                        setIsProfitLossShareModalOpen(!isProfitLossShareModalOpen);
                    }}
                />
            )}
        </>
    );
};

export { PositionCard };
